import { useTypedEvent } from "@faro-lotv/app-component-toolbox";
import { GUID, TypedEvent } from "@faro-lotv/foundation";
import { useCallback, useEffect, useRef } from "react";
import { Vector3 } from "three";
import { ToolCallback } from "./tool-controls-interface";

export type ZoomLensPickingProps = {
  /** True while the tool using the zoom lens is active */
  isActive: boolean;

  /** Callback issued when the hovered point changes */
  currentPointChanged?(point: Vector3 | undefined): void;

  /** Optional signal triggered when the user clicks the zoom lens with a valid 'green' target */
  onValidClickInZoomLens?: TypedEvent<GUID>;

  /** Callback issued with the picked point and the point cloud it belongs to */
  onPointPicked(point: Vector3, pointCloudId: GUID): void;
};

/**
 * Picks 3D points on the point cloud rendered inside the zoom lens.
 * The picked point is the last one hovered, not the one under the cursor at click time.
 *
 * @returns the hover handler the tool has to expose on its ref
 */
export function useZoomLensPicking({
  isActive,
  currentPointChanged,
  onValidClickInZoomLens,
  onPointPicked,
}: ZoomLensPickingProps): ToolCallback {
  // The lens can signal a valid click while the cursor is not over the point cloud
  const hoveredPoint = useRef(new Vector3(NaN, NaN, NaN));

  const pointHovered = useCallback<ToolCallback>(
    (ev) => {
      hoveredPoint.current.copy(ev.point);
      currentPointChanged?.(ev.point);
    },
    [currentPointChanged],
  );

  // A point hovered before the tool was closed must not be picked when it reopens
  useEffect(() => {
    if (!isActive) return;
    const lastHoveredPoint = hoveredPoint.current;
    return () => {
      lastHoveredPoint.set(NaN, NaN, NaN);
      currentPointChanged?.(undefined);
    };
  }, [isActive, currentPointChanged]);

  // When the zoom lens signals a valid click, call the onPointPicked callback with the last hovered point and the point cloud id
  const onValidClick = useCallback(
    (pointCloudId: GUID) => {
      if (isNaN(hoveredPoint.current.x)) return;
      onPointPicked(hoveredPoint.current, pointCloudId);
    },
    [onPointPicked],
  );
  useTypedEvent(isActive ? onValidClickInZoomLens : undefined, onValidClick);

  return pointHovered;
}
